import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border py-0 font-medium whitespace-nowrap transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&>svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "border-badge-primary/20 bg-badge-primary/8 text-badge-primary-foreground",
        outline:
          "border-badge-outline-border bg-badge-outline text-badge-outline-foreground",
        secondary:
          "border-transparent bg-badge-secondary text-badge-secondary-foreground",
        success:
          "border-badge-green/20 bg-badge-green/8 text-badge-green-foreground",
        warning:
          "border-badge-amber/20 bg-badge-amber/8 text-badge-amber-foreground",
        danger: "border-badge-red/20 bg-badge-red/8 text-badge-red-foreground",
        // Reads --ds-tone/--ds-foreground from an enclosing data set accent class.
        accent:
          "border-[hsl(var(--ds-tone)_/_0.2)] bg-[hsl(var(--ds-tone)_/_0.08)] text-[hsl(var(--ds-foreground))]",
        info: "border-badge-sky/20 bg-badge-sky/8 text-badge-sky-foreground",
        model:
          "border-badge-violet/20 bg-badge-violet/8 text-badge-violet-foreground normal-case",
        worker:
          "border-badge-amber/20 bg-badge-amber/8 text-badge-amber-foreground normal-case",
        live: "border-badge-teal/20 bg-badge-teal/8 text-badge-teal-foreground normal-case",
        queued:
          "border-badge-orange/20 bg-badge-orange/8 text-badge-orange-foreground",
        completed:
          "border-badge-green/20 bg-badge-green/8 text-badge-green-foreground",
        plugin:
          "border-badge-sky/20 bg-badge-sky/8 text-badge-sky-foreground normal-case",
        skill:
          "border-badge-violet/20 bg-badge-violet/8 text-badge-violet-foreground normal-case",
        tool: "border-badge-green/20 bg-badge-green/8 text-badge-green-foreground normal-case",
        event: "border-border bg-transparent text-muted-foreground",
        datetime:
          "border-transparent bg-transparent text-muted-foreground italic normal-case tabular-nums",
      },
      size: {
        default: "h-5.5 px-1.5 text-sm has-[svg]:px-1.5 [&>svg]:size-3",
        xs: "h-4 gap-0.5 px-1 text-3xs has-[svg]:px-1 [&>svg]:size-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Badge({
  className,
  variant = "default",
  size = "default",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  const defaultProps = {
    className: cn(badgeVariants({ variant, size }), className),
    "data-slot": "badge",
    "data-variant": variant,
  };
  return useRender({
    defaultTagName: "span",
    render,
    props: mergeProps<"span">(defaultProps, props),
  });
}

export { Badge, badgeVariants };
